import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, ChevronLeft, ChevronRight, Layout, X, GripVertical, Smile } from 'lucide-react';
import { toast } from 'sonner';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_TYPES = [
  { type: 'appointments', label: 'Appointments', icon: '📅', defaultRows: 3 },
  { type: 'mood', label: 'Mood', icon: '😊', defaultRows: 1 },
  { type: 'tasks', label: 'Tasks', icon: '✓', defaultRows: 8 },
  { type: 'meals', label: 'Meals (B/L/D/S)', icon: '🍽️', defaultRows: 4 },
  { type: 'notes', label: 'Notes', icon: '📝', defaultRows: 3 },
  { type: 'water', label: 'Water', icon: '💧', defaultRows: 1 },
  { type: 'gratitude', label: 'Gratitude', icon: '🙏', defaultRows: 3 },
  { type: 'custom', label: 'Custom Section', icon: '✏️', defaultRows: 4 },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MOODS = ['😢', '😐', '😊', '😄'];

function getWeekStart(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
  dt.setDate(diff);
  return dt.toISOString().split('T')[0];
}

export default function CustomOverviews() {
  const [overviews, setOverviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewData, setViewData] = useState({});
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [newName, setNewName] = useState('');
  const [newSections, setNewSections] = useState([]);

  const fetchOverviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/overviews`, { withCredentials: true });
      setOverviews(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOverviews(); }, [fetchOverviews]);

  const fetchData = useCallback(async (ovId, ws) => {
    try {
      const { data } = await axios.get(`${API}/overviews/${ovId}/data/${ws}`, { withCredentials: true });
      setViewData(data.data || {});
    } catch (e) { setViewData({}); }
  }, []);

  useEffect(() => { if (viewing) fetchData(viewing.id, weekStart); }, [viewing, weekStart, fetchData]);

  const handleCreate = async () => {
    if (!newName.trim() || newSections.length === 0) { toast.error('Name and at least 1 section required'); return; }
    try {
      await axios.post(`${API}/overviews`, { name: newName, layout: 'weekly', sections: newSections }, { withCredentials: true });
      toast.success('Overview created!');
      setCreating(false); setNewName(''); setNewSections([]);
      fetchOverviews();
    } catch (e) { toast.error('Failed to create'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/overviews/${id}`, { withCredentials: true });
      toast.success('Deleted'); setViewing(null); fetchOverviews();
    } catch (e) { toast.error('Failed'); }
  };

  const handleSaveData = async () => {
    if (!viewing) return;
    try {
      await axios.post(`${API}/overviews/data`, { overview_id: viewing.id, week_start: weekStart, data: viewData }, { withCredentials: true });
      toast.success('Saved!');
    } catch (e) { toast.error('Failed to save'); }
  };

  const updateCell = (sectionIdx, day, rowIdx, value) => {
    const key = `${sectionIdx}-${day}-${rowIdx}`;
    setViewData(prev => ({ ...prev, [key]: value }));
  };

  const getCell = (sectionIdx, day, rowIdx) => viewData[`${sectionIdx}-${day}-${rowIdx}`] || '';

  const addSection = (type) => {
    const cfg = SECTION_TYPES.find(s => s.type === type) || SECTION_TYPES[7];
    setNewSections([...newSections, { type, label: cfg.label, rows: cfg.defaultRows }]);
  };

  const removeSection = (idx) => setNewSections(newSections.filter((_, i) => i !== idx));
  const updateSectionLabel = (idx, label) => { const arr = [...newSections]; arr[idx] = { ...arr[idx], label }; setNewSections(arr); };
  const updateSectionRows = (idx, rows) => { const arr = [...newSections]; arr[idx] = { ...arr[idx], rows: Math.max(1, Math.min(15, rows)) }; setNewSections(arr); };

  const navigateWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const weekDates = DAYS.map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return { day: DAYS[i], date: d.toISOString().split('T')[0], num: d.getDate() };
  });

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading...</div>;

  // ============== VIEWING AN OVERVIEW ==============
  if (viewing) {
    return (
      <div className="space-y-4" data-testid="overview-viewer">
        <div className="flex items-center justify-between">
          <button onClick={() => setViewing(null)} className="neo-btn px-3 py-2 bg-white dark:bg-[#252545]" data-testid="overview-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold font-['Fredoka'] italic">{viewing.name}</h2>
          <div className="flex gap-2">
            <button onClick={handleSaveData} className="neo-btn px-4 py-2 bg-[#B9FBC0] flex items-center gap-1" data-testid="overview-save-data">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => handleDelete(viewing.id)} className="neo-btn px-3 py-2 bg-[#FF6B6B] text-white" data-testid="overview-delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Week navigation */}
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => navigateWeek(-1)} className="neo-btn p-2 bg-white dark:bg-[#252545]"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-bold font-['Fredoka']">Week of {weekStart}</span>
          <button onClick={() => navigateWeek(1)} className="neo-btn p-2 bg-white dark:bg-[#252545]"><ChevronRight className="w-4 h-4" /></button>
        </div>

        {/* Overview Grid */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="p-2 text-xs font-bold uppercase text-gray-400 w-[100px] border-b-2 border-[#A2D2FF]">Section</th>
                <th className="p-2 text-xs font-bold uppercase text-gray-400 w-[100px] border-b-2 border-[#FFC8DD] bg-[#FFC8DD15]">Weekly</th>
                {weekDates.map(d => (
                  <th key={d.day} className="p-2 text-center border-b-2 border-[#A2D2FF] bg-[#A2D2FF10]">
                    <span className="text-xs font-bold uppercase text-gray-400">{d.day}</span>
                    <span className="block text-sm font-bold">{d.num}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viewing.sections.map((section, sIdx) => (
                <React.Fragment key={sIdx}>
                  {/* Section header row */}
                  <tr className="bg-[#FDF0D520]">
                    <td colSpan={9} className="px-2 pt-3 pb-1 font-bold font-['Fredoka'] text-sm text-[#FFC8DD] border-t-2 border-dashed border-gray-200">
                      {section.label}
                    </td>
                  </tr>
                  {/* Section rows */}
                  {section.type === 'mood' ? (
                    <tr>
                      <td className="p-1" />
                      <td className="p-1" />
                      {weekDates.map((d, di) => (
                        <td key={di} className="p-1">
                          <div className="flex justify-center gap-1">
                            {MOODS.map((m, mi) => (
                              <button key={mi} onClick={() => updateCell(sIdx, d.day, 0, mi.toString())}
                                className={`w-7 h-7 rounded-full text-sm transition-all ${getCell(sIdx, d.day, 0) === mi.toString() ? 'scale-125 ring-2 ring-black' : 'opacity-50'}`}
                                data-testid={`ov-mood-${d.day}-${mi}`}>{m}</button>
                            ))}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ) : section.type === 'meals' ? (
                    ['B', 'L', 'D', 'S'].map((meal, ri) => (
                      <tr key={ri}>
                        <td className="p-1 text-xs font-bold text-gray-400 text-right pr-2">{meal}:</td>
                        <td className="p-1" />
                        {weekDates.map((d, di) => (
                          <td key={di} className="p-0.5">
                            <input type="text" value={getCell(sIdx, d.day, ri)} onChange={(e) => updateCell(sIdx, d.day, ri, e.target.value)}
                              className="w-full bg-[#A2D2FF10] border-b border-dotted border-gray-300 px-1 py-0.5 text-xs focus:outline-none" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : section.type === 'water' ? (
                    <tr>
                      <td className="p-1" />
                      <td className="p-1" />
                      {weekDates.map((d, di) => {
                        const val = parseInt(getCell(sIdx, d.day, 0)) || 0;
                        return (
                          <td key={di} className="p-1">
                            <div className="flex justify-center gap-0.5">
                              {Array.from({ length: 8 }).map((_, i) => (
                                <button key={i} onClick={() => updateCell(sIdx, d.day, 0, (val === i + 1 ? i : i + 1).toString())}
                                  className={`w-3 h-4 rounded-sm border ${i < val ? 'bg-blue-300 border-blue-400' : 'bg-gray-100 border-gray-300'}`} />
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ) : (
                    Array.from({ length: section.rows }).map((_, ri) => (
                      <tr key={ri}>
                        <td className="p-0.5" />
                        {/* Weekly column */}
                        <td className="p-0.5">
                          {ri === 0 || section.type === 'tasks' ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => { const v = getCell(sIdx, 'weekly', ri); updateCell(sIdx, 'weekly', ri, v === '1' ? '' : '1'); }}
                                className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-[8px] ${getCell(sIdx, 'weekly', ri) === '1' ? 'bg-[#B9FBC0] border-black' : 'border-gray-300'}`}>
                                {getCell(sIdx, 'weekly', ri) === '1' ? '✓' : ''}
                              </button>
                              <input type="text" value={getCell(sIdx, 'weekly', ri + 100) || ''}
                                onChange={(e) => updateCell(sIdx, 'weekly', ri + 100, e.target.value)}
                                className="w-full bg-transparent border-b border-dotted border-gray-200 px-0.5 py-0 text-xs focus:outline-none" />
                            </div>
                          ) : null}
                        </td>
                        {weekDates.map((d, di) => (
                          <td key={di} className="p-0.5">
                            {section.type === 'tasks' ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => { const v = getCell(sIdx, d.day, ri); updateCell(sIdx, d.day, ri, v === '1' ? '' : '1'); }}
                                  className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-[8px] ${getCell(sIdx, d.day, ri) === '1' ? 'bg-[#B9FBC0] border-black' : 'border-gray-300'}`}
                                  data-testid={`ov-task-${d.day}-${ri}`}>
                                  {getCell(sIdx, d.day, ri) === '1' ? '✓' : ''}
                                </button>
                                <input type="text" value={getCell(sIdx, d.day, ri + 100) || ''}
                                  onChange={(e) => updateCell(sIdx, d.day, ri + 100, e.target.value)}
                                  className="w-full bg-transparent border-b border-dotted border-gray-200 px-0.5 py-0 text-xs focus:outline-none" />
                              </div>
                            ) : (
                              <input type="text" value={getCell(sIdx, d.day, ri)} onChange={(e) => updateCell(sIdx, d.day, ri, e.target.value)}
                                className="w-full bg-[#A2D2FF08] border-b border-dotted border-gray-200 px-1 py-0.5 text-xs focus:outline-none" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ============== OVERVIEW LIST + CREATOR ==============
  return (
    <div className="space-y-4" data-testid="custom-overviews">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
          <Layout className="w-6 h-6" /> Custom Overviews
        </h2>
        <button onClick={() => setCreating(!creating)} className="neo-btn px-4 py-2 bg-[#A2D2FF] flex items-center gap-2" data-testid="create-overview-btn">
          <Plus className="w-4 h-4" /> New Overview
        </button>
      </div>
      <DoodleSquiggle width={100} color="#CDB4DB" />

      {/* Creator */}
      {creating && (
        <div className="neo-card p-5 animate-bounce-in" data-testid="overview-creator">
          <h3 className="font-bold font-['Fredoka'] text-lg mb-3">Create Overview</h3>
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
            className="neo-input w-full mb-3" placeholder="Overview name (e.g., Weekly Planner)" data-testid="overview-name-input" />

          <p className="text-sm font-bold mb-2">Add sections:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {SECTION_TYPES.map(s => (
              <button key={s.type} onClick={() => addSection(s.type)}
                className="neo-btn px-3 py-1.5 bg-white dark:bg-[#252545] text-sm flex items-center gap-1"
                data-testid={`add-section-${s.type}`}>
                <span>{s.icon}</span> {s.label}
              </button>
            ))}
          </div>

          {/* Selected sections */}
          {newSections.length > 0 && (
            <div className="space-y-2 mb-4">
              {newSections.map((s, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-[#2a2a4a] rounded-lg">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <input type="text" value={s.label} onChange={(e) => updateSectionLabel(i, e.target.value)}
                    className="neo-input flex-1 !py-1 text-sm" />
                  <label className="text-xs text-gray-500">Rows:</label>
                  <input type="number" value={s.rows} onChange={(e) => updateSectionRows(i, parseInt(e.target.value) || 1)}
                    className="neo-input w-14 !py-1 text-sm text-center" min={1} max={15} />
                  <button onClick={() => removeSection(i)} className="text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={handleCreate} className="neo-btn flex-1 py-2 bg-[#B9FBC0]" data-testid="save-overview-btn">Create Overview</button>
            <button onClick={() => { setCreating(false); setNewSections([]); }} className="neo-btn px-4 py-2 bg-gray-200">Cancel</button>
          </div>
        </div>
      )}

      {/* Saved Overviews */}
      {overviews.length === 0 && !creating ? (
        <div className="neo-card p-8 text-center">
          <Layout className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-['Fredoka'] text-lg text-gray-500">No custom overviews yet</p>
          <p className="text-sm text-gray-400">Create one to build your perfect planner layout!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {overviews.map(ov => (
            <button key={ov.id} onClick={() => setViewing(ov)}
              className="neo-card p-4 text-left hover:scale-[1.02] transition-all"
              data-testid={`overview-card-${ov.id}`}>
              <h4 className="font-bold font-['Fredoka'] text-lg">{ov.name}</h4>
              <p className="text-xs text-gray-400">{ov.sections.length} sections</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {ov.sections.map((s, i) => {
                  const cfg = SECTION_TYPES.find(st => st.type === s.type);
                  return <span key={i} className="px-2 py-0.5 bg-[#A2D2FF20] rounded-full text-[10px] font-bold">{cfg?.icon} {s.label}</span>;
                })}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Preset: Weekly Planner like the reference */}
      {overviews.length === 0 && !creating && (
        <div className="text-center">
          <button onClick={() => {
            setNewName('Weekly Planner');
            setNewSections([
              { type: 'appointments', label: 'Appointments', rows: 3 },
              { type: 'mood', label: 'Mood', rows: 1 },
              { type: 'tasks', label: "Today's Tasks", rows: 10 },
              { type: 'notes', label: 'Notes', rows: 3 },
              { type: 'meals', label: 'Meals', rows: 4 },
            ]);
            setCreating(true);
          }}
            className="neo-btn px-6 py-3 bg-[#CDB4DB] flex items-center gap-2 mx-auto" data-testid="preset-weekly-btn">
            <Layout className="w-5 h-5" /> Start with Weekly Planner Template
          </button>
        </div>
      )}
    </div>
  );
}
